import {
  Expense,
  ExpenseCategory,
  ExpensePaymentMethod,
} from "@/redux/services/expense/expense.type";

// French labels for the seeded system categories. Until an i18n system lands,
// this is where a system category's translation key is resolved to a display
// label; custom categories render their raw label verbatim. When i18n ships,
// replace this map with the catalog lookup — the keys are already stored.
export const EXPENSE_CATEGORY_LABELS: Record<string, string> = {
  "expense.category.personnel": "Personnel",
  "expense.category.transport": "Transport",
  "expense.category.rent": "Loyer",
  "expense.category.utilities": "Charges (eau, électricité)",
  "expense.category.supplies": "Fournitures",
  "expense.category.maintenance": "Maintenance",
  "expense.category.taxes": "Taxes & impôts",
  "expense.category.cogs": "Marchandises / Stock",
  "expense.category.other": "Autre",
};

export const getExpenseCategoryLabel = (
  category?: Pick<ExpenseCategory, "label" | "translationKey"> | null
): string => {
  if (!category) return "—";
  if (category.label) return category.label;
  if (category.translationKey) {
    return (
      EXPENSE_CATEGORY_LABELS[category.translationKey] ?? category.translationKey
    );
  }
  return "—";
};

export const EXPENSE_PAYMENT_METHODS: {
  value: ExpensePaymentMethod;
  label: string;
}[] = [
  { value: "cash", label: "Espèces" },
  { value: "wave", label: "Wave" },
  { value: "orange money", label: "Orange Money" },
  { value: "card", label: "Carte" },
  { value: "bank transfer", label: "Virement bancaire" },
];

export const getPaymentMethodLabel = (method: ExpensePaymentMethod): string =>
  EXPENSE_PAYMENT_METHODS.find((m) => m.value === method)?.label ?? method;

export const isExpenseVoided = (expense: Expense): boolean =>
  expense.status === "voided";

// French copy for the transient message keys returned by
// POST /api/expenses/{uuid}/void. The API sends only the key (both on the
// success body via `voidNotice` and on the 403 error body); the frontend owns
// the translation. Same convention as EXPENSE_CATEGORY_LABELS above.
export const EXPENSE_VOID_MESSAGES: Record<string, string> = {
  "expense.void.closed_session_record_refund":
    "La session de caisse d'origine est déjà clôturée : aucun mouvement de caisse n'a été enregistré. Si l'argent a été remboursé physiquement, enregistrez une entrée de caisse manuelle.",
  "expense.void.closed_session_manager_only":
    "Seul un manager peut annuler une dépense issue d'une session de caisse déjà clôturée.",
};

export const getExpenseVoidMessage = (
  key?: string | null
): string | undefined => (key ? EXPENSE_VOID_MESSAGES[key] : undefined);

// What the void callout says about the cash drawer (SFRESTO-135). The API
// records a compensating pay-in only while the originating session is open;
// a closed-session void records none. Unknown (older API) → say nothing.
export const getVoidDrawerNote = (expense: Expense): string | undefined => {
  if (!expense.paidFromDrawer) return undefined;
  if (expense.voidedWithDrawerMovement === true) {
    return "Un mouvement de caisse compensatoire (entrée) a été enregistré pour rétablir le fond de caisse.";
  }
  if (expense.voidedWithDrawerMovement === false) {
    return "La session de caisse d'origine était déjà clôturée : aucun mouvement de caisse n'a été enregistré. Si l'argent a été remboursé physiquement, enregistrez une entrée de caisse manuelle.";
  }
  return undefined;
};

// Voiding a drawer-backed expense whose session is closed corrects a counted
// drawer: the API requires a manager (or owner) of the restaurant, whatever
// expenses.void says (admin/support included) (SFRESTO-135).
export const isClosedSessionVoidBlocked = (
  expense: Expense,
  isRestaurantManager: boolean
): boolean =>
  expense.paidFromDrawer &&
  expense.drawerSessionOpen === false &&
  !isRestaurantManager;
